import { DevTool } from "@hookform/devtools";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Terms from "../../components/terms/Terms";
import StepTitleView from "../StepTitleView";

interface FormFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  signUpComment: string;
  termsAccepted: boolean;
}

interface ContactDetailsProps {
  language: Language;
  productId: string;
  onSubmit: (contactDetails: ContactDetails) => void;
  details: ContactDetails;
  product?: Product;
}

const ContactDetails = ({
  language,
  productId,
  onSubmit,
  details,
  product,
}: ContactDetailsProps) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setValue } = useForm<FormFields>({
    mode: "onBlur",
    defaultValues: {
      ...details,
      termsAccepted: false,
    },
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const submit: SubmitHandler<FormFields> = (data) => {
    setFormSubmitting(false);
    const { firstName, lastName, email, password } = data;
    onSubmit({ firstName, lastName, email, password });
  };

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit(submit)}>
      <StepTitleView
        title={t("step.contactDetails.title")}
        descriptions={[t("step.contactDetails.description")]}
        productName={product?.labels?.name || ""}
      />
      <Stack spacing={2}>
        <Controller
          control={control}
          name="firstName"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <TextField
              label={t("step.contactDetails.firstName.label")}
              placeholder={
                t("step.contactDetails.firstName.placeholder") ?? undefined
              }
              fullWidth
              required
              autoFocus
              autoComplete="given-name"
              error={!!fieldState.error}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <TextField
              label={t("step.contactDetails.lastName.label")}
              placeholder={
                t("step.contactDetails.lastName.placeholder") ?? undefined
              }
              fullWidth
              required
              error={!!fieldState.error}
              autoComplete="family-name"
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          rules={{ required: true, pattern: /\S+@\S+\.\S+/ }}
          render={({ field, fieldState }) => (
            <TextField
              required
              label={t("step.contactDetails.email.label")}
              placeholder={
                t("step.contactDetails.email.placeholder") ?? undefined
              }
              fullWidth
              error={!!fieldState.error}
              autoComplete="email"
              type="email"
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          rules={{
            required: true,
            pattern: /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,30}/,
          }}
          render={({ field, fieldState }) => (
            <TextField
              required
              label={t("step.contactDetails.password.label")}
              placeholder={
                t("step.contactDetails.password.placeholder") ?? undefined
              }
              helperText={t("step.contactDetails.password.subLabel")}
              fullWidth
              error={!!fieldState.error}
              autoComplete="new-password"
              {...field}
              InputProps={{
                type: showPassword ? "text" : "password",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Stack>
      <Controller
        control={control}
        name="termsAccepted"
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <FormControl
            error={!!fieldState.error}
            component="fieldset"
            variant="standard"
            sx={{ width: "100%", mt: 3, mb: 1, minHeight: 64 }}
          >
            <Stack direction="row" alignItems="center">
              <Checkbox
                color="primary"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                inputProps={{
                  "aria-label":
                    `${t("step.contactDetails.terms.label.partA")} ${t(
                      "step.contactDetails.terms.label.partB"
                    )}` ?? "",
                }}
              />

              <Typography
                component="span"
                variant="body2"
                sx={{ display: "flex", flexWrap: "wrap" }}
              >
                {t("step.contactDetails.terms.label.partA")}&nbsp;
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setShowTerms(true)}
                  underline="always"
                >
                  {t("step.contactDetails.terms.label.partB")}
                </Link>
              </Typography>
            </Stack>

            {fieldState.error && (
              <FormHelperText sx={{ ml: 5 }}>
                {t("step.contactDetails.terms.isRequired.message")}
              </FormHelperText>
            )}
            <Terms
              open={showTerms}
              language={language}
              productId={productId}
              onClose={() => setShowTerms(false)}
              onAccept={() => {
                setValue("termsAccepted", true, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setShowTerms(false);
              }}
            />
          </FormControl>
        )}
      />
      <LoadingButton
        id="contact-details-get-started"
        type="submit"
        variant="contained"
        loading={formSubmitting}
        fullWidth
      >
        {t("button.getStarted")}
      </LoadingButton>
      <DevTool control={control} />
    </form>
  );
};

export default ContactDetails;
